import React, { useEffect, useState, useMemo, useCallback, useRef } from 'react';
import {
    Input, Label, FormGroup, Button, Modal, Container, Row, Col, Alert, Collapse, InputGroup,
    Card,
    TabContent,
    TabPane,
    UncontrolledTooltip,
    UncontrolledPopover,
    PopoverHeader,
    PopoverBody
} from "reactstrap";
import Loader from "../../loaders";
import NavbarPage from '../../navbars/NavbarPage';
import { AgGridReact } from 'ag-grid-react';
import moment from 'moment';
import { DropzoneList, FileDropzone } from '../../FileDropzone';
import fileDownload from 'js-file-download';
import readXlsxFile, { readSheetNames } from 'read-excel-file';
import { FILE_EXT_XLSX, formatDate, MIME_TYPE_XLSX } from '../../../utils';
import { TooltipButton } from '../../inputs';
import api from "../../../services/api";
import * as XLSX from 'xlsx';
import SweetAlert from 'react-bootstrap-sweetalert';

const pageSize = 100;
const agGridSort = ['asc', 'desc'];
const url = "candidateimport";
const moduleNameProfile = "Profile";
const moduleNameAllocation = "Allocation";
const moduleNameAddress = "Address";
const moduleNameEmergencyContact = "Emergency Contact";

export default function CandidateImportDialog(props) {
    const {
        onClose,
        onSave,
    } = props;
    const [loading, setLoading] = useState(false);
    const [activeTab, setActiveTab] = useState(0);
    const [validated, setValidated] = useState(false);
    const [importFile, setImportFile] = useState(null);
    const [profiles, setProfiles] = useState([]);
    const [profileValidationResult, setProfileValidationResult] = useState([]);
    const [allocations, setAllocations] = useState([]);
    const [allocationValidationResult, setAllocationValidationResult] = useState([]);
    const [addresses, setAddresses] = useState([]);
    const [addressValidationResult, setAddressValidationResult] = useState([]);
    const [emergencyContacts, setEmergencyContacts] = useState([]);
    const [emergencyContactValidationResult, setEmergencyContactValidationResult] = useState([]);
    const [showSubmissionConfirmation, setShowSubmissionConfirmation] = useState(false);
    const gridProfileRef = useRef();
    const gridAllocationRef = useRef();
    const gridAddressRef = useRef();
    const gridEmergencyContactRef = useRef();
    
    const defaultColDef = useMemo(() => ({
        sortable: true,
        resizable: true,
        //filter: true,
        editable: false,
        filter: 'agTextColumnFilter',
        filterParams: {
            buttons: ['reset'],
            debounceMs: 200,
            excelMode: 'mac',
        },
        floatingFilter: true,
        floatingFilterComponentParams: {
            suppressFilterButton: true,
        },
        suppressMenu: true,
    }), []);
    
    const columnProfileDefs = useMemo(() => ([
        {
            field: "rowNumber",
            headerName: "ID",
        },
        {
            field: "validationStatus",
            headerName: "Validation Status",
            cellRenderer: (row) => {
                const {data: rowData} = row;
                const elementKey = `candidate_import_profile_${rowData.rowNumber}`;
                const popoverId = `validation_message_profile_${rowData.rowNumber}`;

                return (
                    <div key={elementKey} id={elementKey} className="text-center">
                        <span id={popoverId}><i className={`fas fa-${rowData.validationStatus === 0 ? "minus" : (rowData.validationStatus === 1 ? "check-circle text-success" : "info-circle text-danger")}`} /></span>
                    {
                        rowData.validationMessage.length ?
                        <UncontrolledPopover target={elementKey} trigger="hover">
                            <PopoverHeader>Error</PopoverHeader>
                            <PopoverBody className="font-size-small-custom">
                                <ul className="m-0 p-0 pl-3">
                                {
                                    rowData.validationMessage.map((m, idx) => {
                                        return (
                                            <li key={`vm_profile_${rowData.rowNumber}_${idx}`}>{m.message}</li>
                                        )
                                    })
                                }
                                </ul>
                            </PopoverBody>
                        </UncontrolledPopover>
                        :
                        null
                    }
                    </div>
                );
            },
        },
        {
            field: "employeeNumber",
            headerName: "Employee Number",
        },
        {
            field: "surname",
            headerName: "Surname",
        },
        {
            field: "firstName",
            headerName: "First Name"
        },
        {
            field: "dob",
            headerName: "Date of Birth"
        },
        {
            field: "mobilePhoneNumber",
            headerName: "Mobile Number",
            valueFormatter: (params) => {
                var { value } = params;

                const match = value?.match(/^([\dX]{4})([\dX]{3})([\dX]{3})$/)
                if (match) {
                    return `${match[1]} ${match[2]} ${match[3]}`;
                } else {
                    return value;
                }
            },
        },
        {
            field: "email",
            headerName: "Email"
        },
        {
            field: "adsReference",
            headerName: "Advertising Reference"
        },
        {
            field: "candidateStatus",
            headerName: "Candidate Status",
        },
    ]), []);
    
    const columnAllocationDefs = useMemo(() => ([
        {
            field: "rowNumber",
            headerName: "ID",
        },
        {
            field: "validationStatus",
            headerName: "Validation Status",
            cellRenderer: (row) => {
                const {data: rowData} = row;
                const elementKey = `candidate_import_allocation_${rowData.rowNumber}`;
                const popoverId = `validation_message_allocation_${rowData.rowNumber}`;

                return (
                    <div key={elementKey} id={elementKey} className="text-center">
                        <span id={popoverId}><i className={`fas fa-${rowData.validationStatus === 0 ? "minus" : (rowData.validationStatus === 1 ? "check-circle text-success" : "info-circle text-danger")}`} /></span>
                    {
                        rowData.validationMessage.length ?
                        <UncontrolledPopover target={elementKey} trigger="hover">
                            <PopoverHeader>Error</PopoverHeader>
                            <PopoverBody className="font-size-small-custom">
                                <ul className="m-0 p-0 pl-3">
                                {
                                    rowData.validationMessage.map((m, idx) => {
                                        return (
                                            <li key={`vm_allocation_${rowData.rowNumber}_${idx}`}>{m.message}</li>
                                        )
                                    })
                                }
                                </ul>
                            </PopoverBody>
                        </UncontrolledPopover>
                        :
                        null
                    }
                    </div>
                );
            },
        },
        {
            field: "employeeNumber",
            headerName: "Employee Number",
        },
        {
            field: "entity",
            headerName: "Entity",
        },
        {
            field: "project",
            headerName: "Project",
        },
        {
            field: "position",
            headerName: "Position"
        },
        {
            field: "startDate",
            headerName: "Start Date"
        },
        {
            field: "endDate",
            headerName: "End Date"
        },
        {
            field: "lastPaidDate",
            headerName: "Last Paid Date"
        },
        {
            field: "active",
            headerName: "Is Active"
        },
    ]), []);
    
    const columnAddressDefs = useMemo(() => ([
        {
            field: "rowNumber",
            headerName: "ID",
        },
        {
            field: "validationStatus",
            headerName: "Validation Status",
            cellRenderer: (row) => {
                const {data: rowData} = row;
                const elementKey = `candidate_import_address_${rowData.rowNumber}`;
                const popoverId = `validation_message_address_${rowData.rowNumber}`;

                return (
                    <div key={elementKey} id={elementKey} className="text-center">
                        <span id={popoverId}><i className={`fas fa-${rowData.validationStatus === 0 ? "minus" : (rowData.validationStatus === 1 ? "check-circle text-success" : "info-circle text-danger")}`} /></span>
                    {
                        rowData.validationMessage.length ?
                        <UncontrolledPopover target={elementKey} trigger="hover">
                            <PopoverHeader>Error</PopoverHeader>
                            <PopoverBody className="font-size-small-custom">
                                <ul className="m-0 p-0 pl-3">
                                {
                                    rowData.validationMessage.map((m, idx) => {
                                        return (
                                            <li key={`vm_address_${rowData.rowNumber}_${idx}`}>{m.message}</li>
                                        )
                                    })
                                }
                                </ul>
                            </PopoverBody>
                        </UncontrolledPopover>
                        :
                        null
                    }
                    </div>
                );
            },
        },
        {
            field: "employeeNumber",
            headerName: "Employee Number",
        },
        {
            field: "mainAddressLine1",
            headerName: "Main Address Line 1",
        },
        {
            field: "mainAddressLine2",
            headerName: "Main Address Line 2",
        },
        {
            field: "mainAddressCountry",
            headerName: "Main Address Country",
        },
        {
            field: "mainAddressState",
            headerName: "Main Address State",
        },
        {
            field: "mainAddressCity",
            headerName: "Main Address City",
        },
        {
            field: "mainAddressPostcode",
            headerName: "Main Address Postcode",
        },
        {
            field: "mailingAddressLine1",
            headerName: "Mailing Address Line 1",
        },
        {
            field: "mailingAddressLine2",
            headerName: "Mailing Address Line 2",
        },
        {
            field: "mailingAddressCountry",
            headerName: "Mailing Address Country",
        },
        {
            field: "mailingAddressState",
            headerName: "Mailing Address State",
        },
        {
            field: "mailingAddressCity",
            headerName: "Mailing Address City",
        },
        {
            field: "mailingAddressPostcode",
            headerName: "Mailing Address Postcode",
        },
    ]), []);
    
    const columnEmergencyContactDefs = useMemo(() => ([
        {
            field: "rowNumber",
            headerName: "ID",
        },
        {
            field: "validationStatus",
            headerName: "Validation Status",
            cellRenderer: (row) => {
                const {data: rowData} = row;
                const elementKey = `candidate_import_emergency_contact_${rowData.rowNumber}`;
                const popoverId = `validation_message_emergency_contact_${rowData.rowNumber}`;

                return (
                    <div key={elementKey} id={elementKey} className="text-center">
                        <span id={popoverId}><i className={`fas fa-${rowData.validationStatus === 0 ? "minus" : (rowData.validationStatus === 1 ? "check-circle text-success" : "info-circle text-danger")}`} /></span>
                    {
                        rowData.validationMessage.length ?
                        <UncontrolledPopover target={elementKey} trigger="hover">
                            <PopoverHeader>Error</PopoverHeader>
                            <PopoverBody className="font-size-small-custom">
                                <ul className="m-0 p-0 pl-3">
                                {
                                    rowData.validationMessage.map((m, idx) => {
                                        return (
                                            <li key={`vm_emergency_contact_${rowData.rowNumber}_${idx}`}>{m.message}</li>
                                        )
                                    })
                                }
                                </ul>
                            </PopoverBody>
                        </UncontrolledPopover>
                        :
                        null
                    }
                    </div>
                );
            },
        },
        {
            field: "employeeNumber",
            headerName: "Employee Number",
        },
        {
            field: "contact",
            headerName: "Contact Name",
        },
        {
            field: "relation",
            headerName: "Relation"
        },
        {
            field: "mobilePhoneNumber",
            headerName: "Mobile Number",
            valueFormatter: (params) => {
                var { value } = params;

                const match = value?.match(/^([\dX]{4})([\dX]{3})([\dX]{3})$/)
                if (match) {
                    return `${match[1]} ${match[2]} ${match[3]}`;
                } else {
                    return value;
                }
            },
        },
    ]), []);

    const isProfileGridVisible = useMemo(() => {
        const el = gridProfileRef.current;
        return el && el.offsetParent !== null ? true : false;
    }, []);

    const isAllocationGridVisible = useMemo(() => {
        const el = gridAllocationRef.current;
        return el && el.offsetParent !== null ? true : false;
    }, []);

    const isAddressGridVisible = useMemo(() => {
        const el = gridAddressRef.current;
        return el && el.offsetParent !== null ? true : false;
    }, []);

    const isEmergencyContactGridVisible = useMemo(() => {
        const el = gridEmergencyContactRef.current;
        return el && el.offsetParent !== null ? true : false;
    }, []);
    
    const resizeColumns = (ref) => {
        if (!ref?.current?.api || ref?.current?.offsetParent === null)
            return;
        
        ref.current.api.sizeColumnsToFit();
    };

    useEffect(() => {
        if (!isProfileGridVisible)
            return;

        resizeColumns(gridProfileRef);
    }, [isProfileGridVisible]);

    useEffect(() => {
        if (!isAllocationGridVisible)
            return;

        resizeColumns(gridAllocationRef);
    }, [isAllocationGridVisible]);

    useEffect(() => {
        if (!isAddressGridVisible)
            return;

        resizeColumns(gridAddressRef);
    }, [isAddressGridVisible]);

    useEffect(() => {
        if (!isEmergencyContactGridVisible)
            return;

        resizeColumns(gridEmergencyContactRef);
    }, [isEmergencyContactGridVisible]);

    useEffect(() => {
        const handleResize = () => {
            resizeColumns(gridProfileRef);
            resizeColumns(gridAllocationRef);
            resizeColumns(gridAddressRef);
            resizeColumns(gridEmergencyContactRef);
        };

        window.addEventListener('resize', handleResize);

        return () => {
            window.removeEventListener('resize', handleResize);
        };
    }, []);

    const downloadAttachment = (fileInfo) => {
        fileDownload(fileInfo, fileInfo.name);
    }
    
    const files = useMemo(() => {
        if (importFile)
            return (
                <DropzoneList
                    name={importFile.name}
                    size={importFile.size}
                    download={downloadAttachment}
                    itemInEdit={importFile}
                />
            );

        return null;
    }, [importFile]);
    
    const onDrop = useCallback(acceptedFiles => {
        setImportFile(acceptedFiles.length ? acceptedFiles[0] : null)
    }, []);

    const downloadTemplate = () => {
        setLoading(true);
        api.get(`${url}/download/template`, { responseType: 'blob' })
            .then(blob => {
                fileDownload(blob.data, "Candidate Import Template.xlsx");
            })
            .catch(error => {
                console.error({error});
            })
            .finally(() => setLoading(false));
    };
    
    const handleFileSelected = useCallback(() => {
        setLoading(true);
        const file = importFile;

        file.arrayBuffer()
            .then(buffer => {
                const workbook = XLSX.read(buffer, { type: 'array' });

                const sheets = workbook.Workbook.Sheets.map(sheet => ({
                    name: sheet.name,
                    hidden: sheet.Hidden || 0,
                }));

                const visibleSheets = sheets.filter(sheet => sheet.hidden === 0);

                const readCalls = visibleSheets.map(sheet =>
                    readXlsxFile(file, {sheet: sheet.name})
                );

                return Promise.all(readCalls)
                    .then(rows => ({
                        rows,
                        visibleSheets,
                    }));
            })
            .then(({rows, visibleSheets}) => {
                const rawResult = visibleSheets.map((sheet, index) => ({
                    sheetName: sheet.name,
                    rows: rows[index],
                }));

                const rawProfileRows = rawResult.filter(r => r.sheetName === moduleNameProfile).map(r => r.rows)[0].slice(1);
                const newProfileRows = [];

                if (rawProfileRows.length)
                {
                    for (let i = 0; i < rawProfileRows.length; i++) {
                        const row = rawProfileRows[i];
                        newProfileRows.push({
                            rowNumber: i + 1,
                            employeeNumber: row[0] ? `${row[0]}` : null,
                            surname: row[1] ? `${row[1]}` : null,
                            firstName: row[2] ? `${row[2]}` : null,
                            dob: formatDate(row[3], "YYYY-MM-DD"),
                            mobilePhoneNumber: row[4] ? `${row[4]}` : null,
                            email: row[5] ? `${row[5]}` : null,
                            adsReference: row[6] ? `${row[6]}` : null,
                            candidateStatus: row[7] ? `${row[7]}` : null,
                            validationStatus: 0,
                            validationMessage: [],
                        });
                    }
                }

                const rawAllocationRows = rawResult.filter(r => r.sheetName === moduleNameAllocation).map(r => r.rows)[0].slice(1);
                const newAllocationRows = [];

                if (rawAllocationRows.length)
                {
                    for (let i = 0; i < rawAllocationRows.length; i++) {
                        const row = rawAllocationRows[i];
                        newAllocationRows.push({
                            rowNumber: i + 1,
                            employeeNumber: row[0] ? `${row[0]}` : null,
                            entity: row[1] ? `${row[1]}` : null,
                            project: row[2] ? `${row[2]}` : null,
                            position: row[3] ? `${row[3]}` : null,
                            startDate: formatDate(row[4], "YYYY-MM-DD"),
                            endDate: formatDate(row[5], "YYYY-MM-DD"),
                            lastPaidDate: formatDate(row[6], "YYYY-MM-DD"),
                            active: row[7] ?? "N",
                            validationStatus: 0,
                            validationMessage: [],
                        });
                    }
                }

                const rawAddressRows = rawResult.filter(r => r.sheetName === moduleNameAddress).map(r => r.rows)[0].slice(1);
                const newAddressRows = [];

                if (rawAddressRows.length)
                {
                    for (let i = 0; i < rawAddressRows.length; i++) {
                        const row = rawAddressRows[i];
                        newAddressRows.push({
                            rowNumber: i + 1,
                            employeeNumber: row[0] ? `${row[0]}` : null,
                            mainAddressLine1: row[1] ? `${row[1]}` : null,
                            mainAddressLine2: row[2] ? `${row[2]}` : null,
                            mainAddressCountry: row[3] ? `${row[3]}` : null,
                            mainAddressState: row[4] ? `${row[4]}` : null,
                            mainAddressCity: row[5] ? `${row[5]}` : null,
                            mainAddressPostcode: row[6] ? `${row[6]}` : null,
                            mailingAddressLine1: row[7] ? `${row[7]}` : null,
                            mailingAddressLine2: row[8] ? `${row[8]}` : null,
                            mailingAddressCountry: row[9] ? `${row[9]}` : null,
                            mailingAddressState: row[10] ? `${row[10]}` : null,
                            mailingAddressCity: row[11] ? `${row[11]}` : null,
                            mailingAddressPostcode: row[12] ? `${row[12]}` : null,
                            validationStatus: 0,
                            validationMessage: [],
                        });
                    }
                }

                const rawEmergencyContactRows = rawResult.filter(r => r.sheetName === moduleNameEmergencyContact).map(r => r.rows)[0].slice(1);
                const newEmergencyContactRows = [];

                if (rawEmergencyContactRows.length)
                {
                    for (let i = 0; i < rawEmergencyContactRows.length; i++) {
                        const row = rawEmergencyContactRows[i];
                        newEmergencyContactRows.push({
                            rowNumber: i + 1,
                            employeeNumber: row[0] ? `${row[0]}` : null,
                            contact: row[1] ? `${row[1]}` : null,
                            relation: row[2] ? `${row[2]}` : null,
                            mobilePhoneNumber: row[3] ? `${row[3]}` : null,
                            validationStatus: 0,
                            validationMessage: [],
                        });
                    }
                }

                setProfiles(newProfileRows);
                setProfileValidationResult([]);
                setAllocations(newAllocationRows);
                setAllocationValidationResult([]);
                setAddresses(newAddressRows);
                setAddressValidationResult([]);
                setEmergencyContacts(newEmergencyContactRows);
                setEmergencyContactValidationResult([]);
                setValidated(false);
            })
            .catch(e => {
                console.error({e});
            })
            .finally(() => setLoading(false))
        ;
    }, [importFile]);

    useEffect(() => {
        if (!importFile)
            return;

        handleFileSelected();
    }, [importFile]);
    
    const onGridReady = useCallback((params) => {
    }, []);

    const handleValidate = useCallback(() => {
        if (validated)
            return;

        setLoading(true);

        api.post(`${url}/validate`, JSON.stringify({profiles, allocations, addresses, emergencyContacts}))
            .then(response => {
                const {data: validationResult} = response;

                if (!validationResult.length) {
                    setProfiles(JSON.parse(JSON.stringify(profiles.map(p => ({...p, validationStatus: 1, validationMessage: []})))));
                    setProfileValidationResult([]);
                    setAllocations(JSON.parse(JSON.stringify(allocations.map(p => ({...p, validationStatus: 1, validationMessage: []})))));
                    setAllocationValidationResult([]);
                    setAddresses(JSON.parse(JSON.stringify(addresses.map(p => ({...p, validationStatus: 1, validationMessage: []})))));
                    setAddressValidationResult([]);
                    setEmergencyContacts(JSON.parse(JSON.stringify(emergencyContacts.map(p => ({...p, validationStatus: 1, validationMessage: []})))));
                    setEmergencyContactValidationResult([]);
                    setValidated(true);
                    return;
                }

                setProfiles(JSON.parse(JSON.stringify(profiles.map(p => {
                    const currentValidationResults = validationResult.filter(v => v.moduleName === moduleNameProfile && v.invalidProfileIds.includes(p.rowNumber));
                    return {...p, validationStatus: currentValidationResults.length ? -1 : 1, validationMessage: currentValidationResults};
                }))));
                setProfileValidationResult(validationResult.filter(v => v.moduleName === moduleNameProfile && !v.invalidProfileIds.length));
                setAllocations(JSON.parse(JSON.stringify(allocations.map(p => {
                    const currentValidationResults = validationResult.filter(v => v.moduleName === moduleNameAllocation && v.invalidAllocationIds.includes(p.rowNumber));
                    return {...p, validationStatus: currentValidationResults.length ? -1 : 1, validationMessage: currentValidationResults};
                }))));
                setAllocationValidationResult(validationResult.filter(v => v.moduleName === moduleNameAllocation && !v.invalidAllocationIds.length));
                setAddresses(JSON.parse(JSON.stringify(addresses.map(p => {
                    const currentValidationResults = validationResult.filter(v => v.moduleName === moduleNameAddress && v.invalidAddressIds.includes(p.rowNumber));
                    return {...p, validationStatus: currentValidationResults.length ? -1 : 1, validationMessage: currentValidationResults};
                }))));
                setAddressValidationResult(validationResult.filter(v => v.moduleName === moduleNameAddress && !v.invalidAddressIds.length));
                setEmergencyContacts(JSON.parse(JSON.stringify(emergencyContacts.map(p => {
                    const currentValidationResults = validationResult.filter(v => v.moduleName === moduleNameEmergencyContact && v.invalidEmergencyContactIds.includes(p.rowNumber));
                    return {...p, validationStatus: currentValidationResults.length ? -1 : 1, validationMessage: currentValidationResults};
                }))));
                setEmergencyContactValidationResult(validationResult.filter(v => v.moduleName === moduleNameEmergencyContact && !v.invalidEmergencyContactIds.length));
                setValidated(false);
            })
            .catch(error => console.error({error}))
            .finally(() => setLoading(false))
        ;
    }, [validated, profiles, allocations, addresses, emergencyContacts]);

    const handleSave = useCallback(() => {
        if (!validated)
            return;

        setLoading(true);

        api.post(`${url}/submit`, JSON.stringify({profiles, allocations, addresses, emergencyContacts}))
            .then(() => {
                setLoading(false);
                onSave();
            })
            .catch((e) => {
                console.error({e});
                setLoading(false);
            })
        ;
    }, [validated, profiles, allocations, addresses, emergencyContacts]);

    return (
        <Modal
            isOpen={true}
            className="modal-xl"
            modalClassName="db-example-modal-xl"
        >
            {loading && <Loader />}
            <div className="modal-header">
                <h5 className="modal-title">
                    Import Talents
                </h5>
                <button
                    aria-label="Close"
                    className="close"
                    onClick={onClose}
                    type="button"
                >
                    <span aria-hidden={true}><i className="fas fa-times-circle" /></span>
                </button>
            </div>
            <Container
                fluid
                className="w-100 d-flex flex-row justify-content-between align-items-center pt-3 border-bottom border-light pb-3"
            >
                <Row className="w-100 m-0 p-0">
                    <Col xs={12} className="m-0 p-0">
                        <Label>
                            Select File
                            <span className="text-danger">*</span>
                        </Label>
                        <TooltipButton
                            id="download_template"
                            title="Download Template"
                            className="btn-icon ml-2"
                            color="default"
                            size="sm"
                            type="button"
                            onClick={() => downloadTemplate()}
                        >
                            <i className="fa fa-file-excel mr-1" />Download Template
                        </TooltipButton>
                        <FileDropzone
                            files={files}
                            onDrop={onDrop}
                            maxFiles={1}
                            accept={'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'}
                        />
                    </Col>
                </Row>
            </Container>
            <NavbarPage
                name='talent'
                onTabClick={(t) => {
                    setActiveTab(t.tabIndex);
                }}
                tabs={[
                    {
                        title: moduleNameProfile,
                        tabIndex: 0,
                        notifDataCount: profileValidationResult.length + profiles.filter(v => v.validationStatus === -1).length,
                    },
                    {
                        title: moduleNameAllocation,
                        tabIndex: 1,
                        notifDataCount: allocationValidationResult.length + allocations.filter(v => v.validationStatus === -1).length,
                    },
                    {
                        title: moduleNameAddress,
                        tabIndex: 2,
                        notifDataCount: addressValidationResult.length + addresses.filter(v => v.validationStatus === -1).length,
                    },
                    {
                        title: moduleNameEmergencyContact,
                        tabIndex: 3,
                        notifDataCount: emergencyContactValidationResult.length + emergencyContacts.filter(v => v.validationStatus === -1).length,
                    },
                ]}
                defaultActiveTab={0}
            />
            <div className="modal-body">
                <Container fluid className='px-0'>
                    <Row>
                        <Col md={12} className="m-0 p-0">
                            <div className="nav-wrapper pt-0 h-100 d-flex flex-column">
                                <Card className="card-plain no-transition">
                                    <TabContent className="mt-2" activeTab={activeTab}>
                                        <TabPane tabId={0}>
                                        {
                                            profileValidationResult.length ?
                                            <div className="w-100 mb-2 d-flex flex-column">
                                            {
                                                profileValidationResult.map((v, idx) => {
                                                    return (
                                                        <span className="text-default font-size-small-custom" key={`profile_validation_${idx}`}>{v.message}</span>
                                                    )
                                                })
                                            }
                                            </div>
                                            :
                                            null
                                        }
                                            <div className="ag-theme-alpine ag-grid clickable-row w-100" style={{ height: '45vh' }}>
                                                <AgGridReact
                                                    ref={gridProfileRef}
                                                    columnDefs={columnProfileDefs}
                                                    defaultColDef={defaultColDef}
                                                    suppressColumnVirtualisation={false}
                                                    suppressRowVirtualisation={false}
                                                    debounceVerticalScrollbar={false}
                                                    rowBuffer={20}
                                                    headerHeight={30}
                                                    groupHeaderHeight={30}
                                                    rowHeight={30}
                                                    floatingFiltersHeight={30}
                                                    onRowDataUpdated={() => resizeColumns(gridProfileRef)}
                                                    suppressDragLeaveHidesColumns={true}
                                                    suppressColumnMoveAnimation={true}
                                                    animateRows={false}
                                                    pagination={true}
                                                    paginationPageSize={pageSize}
                                                    cacheBlockSize={pageSize}
                                                    onGridReady={onGridReady}
                                                    sortingOrder={agGridSort}
                                                    suppressRowClickSelection={true}
                                                    rowData={profiles}
                                                />
                                            </div>
                                        </TabPane>
                                        <TabPane tabId={1}>
                                        {
                                            allocationValidationResult.length ?
                                            <div className="w-100 mb-2 d-flex flex-column">
                                            {
                                                allocationValidationResult.map((v, idx) => {
                                                    return (
                                                        <span className="text-default font-size-small-custom" key={`allocation_validation_${idx}`}>{v.message}</span>
                                                    )
                                                })
                                            }
                                            </div>
                                            :
                                            null
                                        }
                                            <div className="ag-theme-alpine ag-grid clickable-row w-100" style={{ height: '45vh' }}>
                                                <AgGridReact
                                                    ref={gridAllocationRef}
                                                    columnDefs={columnAllocationDefs}
                                                    defaultColDef={defaultColDef}
                                                    suppressColumnVirtualisation={false}
                                                    suppressRowVirtualisation={false}
                                                    debounceVerticalScrollbar={false}
                                                    rowBuffer={20}
                                                    headerHeight={30}
                                                    groupHeaderHeight={30}
                                                    rowHeight={30}
                                                    floatingFiltersHeight={30}
                                                    onRowDataUpdated={() => resizeColumns(gridAllocationRef)}
                                                    suppressDragLeaveHidesColumns={true}
                                                    suppressColumnMoveAnimation={true}
                                                    animateRows={false}
                                                    pagination={true}
                                                    paginationPageSize={pageSize}
                                                    cacheBlockSize={pageSize}
                                                    onGridReady={onGridReady}
                                                    sortingOrder={agGridSort}
                                                    suppressRowClickSelection={true}
                                                    rowData={allocations}
                                                />
                                            </div>
                                        </TabPane>
                                        <TabPane tabId={2}>
                                        {
                                            addressValidationResult.length ?
                                            <div className="w-100 mb-2 d-flex flex-column">
                                            {
                                                addressValidationResult.map((v, idx) => {
                                                    return (
                                                        <span className="text-default font-size-small-custom" key={`address_validation_${idx}`}>{v.message}</span>
                                                    )
                                                })
                                            }
                                            </div>
                                            :
                                            null
                                        }
                                            <div className="ag-theme-alpine ag-grid clickable-row w-100" style={{ height: '45vh' }}>
                                                <AgGridReact
                                                    ref={gridAddressRef}
                                                    columnDefs={columnAddressDefs}
                                                    defaultColDef={defaultColDef}
                                                    suppressColumnVirtualisation={false}
                                                    suppressRowVirtualisation={false}
                                                    debounceVerticalScrollbar={false}
                                                    rowBuffer={20}
                                                    headerHeight={30}
                                                    groupHeaderHeight={30}
                                                    rowHeight={30}
                                                    floatingFiltersHeight={30}
                                                    onRowDataUpdated={() => resizeColumns(gridAddressRef)}
                                                    suppressDragLeaveHidesColumns={true}
                                                    suppressColumnMoveAnimation={true}
                                                    animateRows={false}
                                                    pagination={true}
                                                    paginationPageSize={pageSize}
                                                    cacheBlockSize={pageSize}
                                                    onGridReady={onGridReady}
                                                    sortingOrder={agGridSort}
                                                    suppressRowClickSelection={true}
                                                    rowData={addresses}
                                                />
                                            </div>
                                        </TabPane>
                                        <TabPane tabId={3}>
                                        {
                                            emergencyContactValidationResult.length ?
                                            <div className="w-100 mb-2 d-flex flex-column">
                                            {
                                                emergencyContactValidationResult.map((v, idx) => {
                                                    return (
                                                        <span className="text-default font-size-small-custom" key={`emergency_contact_validation_${idx}`}>{v.message}</span>
                                                    )
                                                })
                                            }
                                            </div>
                                            :
                                            null
                                        }
                                            <div className="ag-theme-alpine ag-grid clickable-row w-100" style={{ height: '45vh' }}>
                                                <AgGridReact
                                                    ref={gridEmergencyContactRef}
                                                    columnDefs={columnEmergencyContactDefs}
                                                    defaultColDef={defaultColDef}
                                                    suppressColumnVirtualisation={false}
                                                    suppressRowVirtualisation={false}
                                                    debounceVerticalScrollbar={false}
                                                    rowBuffer={20}
                                                    headerHeight={30}
                                                    groupHeaderHeight={30}
                                                    rowHeight={30}
                                                    floatingFiltersHeight={30}
                                                    onRowDataUpdated={() => resizeColumns(gridEmergencyContactRef)}
                                                    suppressDragLeaveHidesColumns={true}
                                                    suppressColumnMoveAnimation={true}
                                                    animateRows={false}
                                                    pagination={true}
                                                    paginationPageSize={pageSize}
                                                    cacheBlockSize={pageSize}
                                                    onGridReady={onGridReady}
                                                    sortingOrder={agGridSort}
                                                    suppressRowClickSelection={true}
                                                    rowData={emergencyContacts}
                                                />
                                            </div>
                                        </TabPane>
                                    </TabContent>
                                </Card>
                            </div>
                        </Col>
                    </Row>
                </Container>
            </div>
            <div className="modal-footer">
                <Button
                    color="secondary"
                    onClick={onClose}
                    type="button"
                >
                    Close
                </Button>
                <Button
                    color="default"
                    onClick={() => validated ? setShowSubmissionConfirmation(true) : handleValidate()}
                    type="button"
                >
                {
                    validated ? "Submit" : "Validate"
                }
                </Button>
            </div>
        {
            showSubmissionConfirmation ?
            <SweetAlert
                warning
                confirmBtnText="Yes"
                confirmBtnBsStyle="default"
                showCancel
                title="Submit New Talents"
                onConfirm={() => {
                    setShowSubmissionConfirmation(false);
                    handleSave();
                }}
                onCancel={() => setShowSubmissionConfirmation(null)}
                focusCancelBtn
            >
                Continue submission?
            </SweetAlert>
            :
            null
        }
        </Modal>
    );
}